/**
 * KnowledgeManager.jsx - Aineistot admin view
 *
 * RAG knowledge base management with:
 * - Instance selector to filter by workshop
 * - Base dataset (global) + instance-specific documents
 * - Source quality (credibility, bias) badges
 * - Admin weight slider (0-2)
 * - Exclude toggle
 *
 * Route: /admin/knowledge
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from '../hooks/useTranslation.jsx';
import { useNotice } from '../components/DialogProvider';
import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';
import { Search, FileText, Book, Hash, Calendar, Shield, AlertTriangle, Eye, EyeOff, RefreshCw, Download } from 'lucide-react';
import AdminHeader from '../components/AdminHeader';
import KnowledgeDetailModal from '../components/KnowledgeDetailModal';
import API_ENDPOINTS from '../config/api';

// ─── Animations ──────────────────────────────────────────────────

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
`;

// ─── Layout ──────────────────────────────────────────────────────

const Container = styled.div`
  min-height: 100vh;
  background: linear-gradient(180deg, #0a0a0a 0%, #1a1a2e 100%);
`;

const Content = styled.div`
  padding: 32px 48px;
  max-width: 1400px;
  margin: 0 auto;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 24px;
  gap: 24px;
  flex-wrap: wrap;
`;

const TitleSection = styled.div``;

const Title = styled.h1`
  font-size: 28px;
  font-weight: 700;
  color: #fff;
  margin: 0 0 6px 0;
  display: flex;
  align-items: center;
  gap: 12px;
`;

const Subtitle = styled.p`
  font-size: 14px;
  color: #888;
  margin: 0;
`;

const StatsRow = styled.div`
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
`;

const StatBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 14px;
  background: rgba(147, 51, 234, 0.15);
  border: 1px solid rgba(147, 51, 234, 0.3);
  border-radius: 20px;
  color: #c084fc;
  font-size: 13px;
  font-weight: 500;
`;

// ─── Filters ─────────────────────────────────────────────────────

const Filters = styled.div`
  display: flex;
  gap: 10px;
  margin-bottom: 20px;
  flex-wrap: wrap;
  align-items: center;
`;

const SearchInput = styled.div`
  position: relative;
  flex: 1;
  min-width: 200px;
  max-width: 350px;

  svg { position: absolute; left: 12px; top: 50%; transform: translateY(-50%); color: #666; width: 16px; height: 16px; }
  input {
    width: 100%;
    padding: 10px 14px 10px 38px;
    background: rgba(255, 255, 255, 0.05);
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 8px;
    color: #fff;
    font-size: 13px;
    outline: none;
    transition: border-color 0.2s;
    &:focus { border-color: rgba(147, 51, 234, 0.5); }
    &::placeholder { color: #666; }
  }
`;

const Select = styled.select`
  padding: 10px 32px 10px 12px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  color: #fff;
  font-size: 13px;
  cursor: pointer;
  outline: none;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%23888' stroke-width='2'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 10px center;
  &:focus { border-color: rgba(147, 51, 234, 0.5); }
  option { background: #1a1a2e; color: #fff; }
`;

const InstanceSelect = styled(Select)`
  border-color: rgba(59, 130, 246, 0.3);
  &:focus { border-color: rgba(59, 130, 246, 0.6); }
`;

// ─── Document Grid ───────────────────────────────────────────────

const DocumentGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(360px, 1fr));
  gap: 16px;
`;

const DocumentCard = styled.div`
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.06) 0%, rgba(255, 255, 255, 0.02) 100%);
  border: 1px solid ${props => props.$excluded ? 'rgba(239, 68, 68, 0.2)' : 'rgba(255, 255, 255, 0.1)'};
  border-radius: 12px;
  padding: 18px;
  position: relative;
  overflow: hidden;
  cursor: pointer;
  transition: all 0.2s ease;
  animation: ${fadeIn} 0.3s ease-out;
  opacity: ${props => props.$excluded ? 0.5 : 1};

  &::before {
    content: '';
    position: absolute;
    top: 0; left: 0; right: 0;
    height: 3px;
    background: ${props => props.$categoryColor || 'linear-gradient(90deg, #9333ea, #6366f1)'};
  }

  &:hover {
    border-color: rgba(147, 51, 234, 0.4);
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(147, 51, 234, 0.1);
  }
`;

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 8px;
  gap: 8px;
`;

const CardTitle = styled.h3`
  font-size: 14px;
  font-weight: 600;
  color: #fff;
  margin: 0;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  flex: 1;
`;

const BadgeRow = styled.div`
  display: flex;
  gap: 4px;
  flex-shrink: 0;
`;

const CategoryBadge = styled.span`
  padding: 3px 8px;
  background: ${props => props.$bg || 'rgba(147, 51, 234, 0.2)'};
  color: ${props => props.$color || '#c084fc'};
  border-radius: 10px;
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  white-space: nowrap;
`;

const Publisher = styled.div`
  font-size: 11px;
  color: #888;
  margin: -4px 0 8px 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  display: flex;
  align-items: center;
  gap: 4px;
  &:before {
    content: '@';
    color: #555;
    font-weight: 600;
  }
`;

const PublisherMissing = styled(Publisher)`
  color: #555;
  font-style: italic;
  &:before {
    content: '⚠';
    color: #b45309;
    font-style: normal;
  }
`;

const Synopsis = styled.p`
  font-size: 13px;
  color: #aaa;
  line-height: 1.5;
  margin: 0 0 10px 0;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

// ─── Quality & Weight Row ────────────────────────────────────────

const ControlRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 6px;
`;

const TagRow = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  margin-bottom: 10px;
  flex-wrap: wrap;
`;

const QualityBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 8px;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 500;
  background: ${props => props.$bg};
  color: ${props => props.$color};
`;

const WeightControl = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-left: auto;
  font-size: 11px;
  color: #888;
`;

const WeightSlider = styled.input`
  width: 60px;
  height: 4px;
  -webkit-appearance: none;
  appearance: none;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 2px;
  outline: none;

  &::-webkit-slider-thumb {
    -webkit-appearance: none;
    width: 12px;
    height: 12px;
    border-radius: 50%;
    background: ${props => {
      const v = parseFloat(props.value) || 1;
      if (v < 0.5) return '#ef4444';
      if (v > 1.5) return '#22c55e';
      return '#c084fc';
    }};
    cursor: pointer;
  }
`;

const WeightValue = styled.span`
  min-width: 24px;
  text-align: center;
  font-weight: 600;
  color: ${props => {
    if (props.$v < 0.5) return '#ef4444';
    if (props.$v > 1.5) return '#22c55e';
    return '#c084fc';
  }};
`;

const ExcludeBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 3px 8px;
  border-radius: 6px;
  font-size: 10px;
  font-weight: 500;
  cursor: pointer;
  border: 1px solid;
  transition: all 0.15s;
  background: ${props => props.$excluded ? 'rgba(239, 68, 68, 0.15)' : 'rgba(255, 255, 255, 0.05)'};
  border-color: ${props => props.$excluded ? 'rgba(239, 68, 68, 0.3)' : 'rgba(255, 255, 255, 0.1)'};
  color: ${props => props.$excluded ? '#ef4444' : '#888'};
  &:hover { background: ${props => props.$excluded ? 'rgba(239, 68, 68, 0.25)' : 'rgba(255, 255, 255, 0.1)'}; }
`;

// ─── Card Footer ─────────────────────────────────────────────────

const Stats = styled.div`
  display: flex;
  gap: 12px;
  padding-top: 10px;
  border-top: 1px solid rgba(255, 255, 255, 0.06);
  font-size: 12px;
  color: #555;
`;

const Stat = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  svg { width: 12px; height: 12px; opacity: 0.5; }
`;

// ─── Loading / Empty ─────────────────────────────────────────────

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  padding: 80px;
`;

const Spinner = styled.div`
  width: 36px; height: 36px;
  border: 3px solid rgba(147, 51, 234, 0.2);
  border-top-color: #9333ea;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  @keyframes spin { to { transform: rotate(360deg); } }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 64px;
  color: #666;
  h3 { font-size: 16px; color: #888; margin: 12px 0 6px; }
  p { font-size: 13px; margin: 0; }
`;

// ─── Constants ───────────────────────────────────────────────────

const CATEGORY_COLORS = {
  technology: { bg: 'rgba(59, 130, 246, 0.2)', color: '#60a5fa', gradient: 'linear-gradient(90deg, #3b82f6, #2563eb)' },
  climate: { bg: 'rgba(34, 197, 94, 0.2)', color: '#4ade80', gradient: 'linear-gradient(90deg, #22c55e, #16a34a)' },
  food: { bg: 'rgba(251, 146, 60, 0.2)', color: '#fb923c', gradient: 'linear-gradient(90deg, #f97316, #ea580c)' },
  society: { bg: 'rgba(236, 72, 153, 0.2)', color: '#f472b6', gradient: 'linear-gradient(90deg, #ec4899, #db2777)' },
  economy: { bg: 'rgba(250, 204, 21, 0.2)', color: '#facc15', gradient: 'linear-gradient(90deg, #eab308, #ca8a04)' },
  education: { bg: 'rgba(168, 85, 247, 0.2)', color: '#c084fc', gradient: 'linear-gradient(90deg, #a855f7, #9333ea)' },
  immigration: { bg: 'rgba(20, 184, 166, 0.2)', color: '#2dd4bf', gradient: 'linear-gradient(90deg, #14b8a6, #0d9488)' },
  employment: { bg: 'rgba(244, 114, 182, 0.2)', color: '#f472b6', gradient: 'linear-gradient(90deg, #ec4899, #be185d)' },
  general: { bg: 'rgba(147, 51, 234, 0.2)', color: '#c084fc', gradient: 'linear-gradient(90deg, #9333ea, #7c3aed)' },
};

const formatDate = (d) => d ? new Date(d).toLocaleDateString('fi-FI', { day: 'numeric', month: 'short', year: 'numeric' }) : '-';
const formatWordCount = (c) => c >= 1000 ? `${(c / 1000).toFixed(1)}k` : (c || '-');

function credibilityBadge(sq) {
  const c = sq?.credibility_score;
  if (c == null) return null;
  if (c >= 0.7) return { label: `${(c * 100).toFixed(0)}%`, bg: 'rgba(34, 197, 94, 0.15)', color: '#4ade80' };
  if (c >= 0.4) return { label: `${(c * 100).toFixed(0)}%`, bg: 'rgba(250, 204, 21, 0.15)', color: '#facc15' };
  return { label: `${(c * 100).toFixed(0)}%`, bg: 'rgba(239, 68, 68, 0.15)', color: '#f87171' };
}

function biasBadge(sq) {
  const b = sq?.bias_direction;
  if (!b || b === 'neutral') return null;
  return { label: b, bg: 'rgba(251, 146, 60, 0.15)', color: '#fb923c' };
}

// ─── Component ───────────────────────────────────────────────────

const KnowledgeManager = ({ tabs = null }) => {
  const { t } = useTranslation();
  const notice = useNotice();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [metadata, setMetadata] = useState(null);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [instances, setInstances] = useState([]);

  // Filters
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('');
  const [sortBy, setSortBy] = useState('created_at');
  const [instanceId, setInstanceId] = useState('');

  // Load instances for selector
  useEffect(() => {
    fetch(API_ENDPOINTS.INSTANCES())
      .then(r => r.json())
      .then(data => setInstances(Array.isArray(data) ? data : data.instances || []))
      .catch(() => {});
  }, []);

  const handleRagExport = async () => {
    if (!instanceId || instanceId === '__base__') return;
    try {
      const response = await fetch(API_ENDPOINTS.INSTANCE_RAG_EXPORT(instanceId), {
        credentials: 'include',
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.error || `Export failed (${response.status})`);
      }
      const blob = await response.blob();
      const filename = response.headers.get('Content-Disposition')?.match(/filename="(.+)"/)?.[1]
        || `kuutio-rag-export-${instanceId}.json`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('RAG export failed:', err);
      notice.error(t('knowledgeManager.exportFailed', { error: err.message }));
    }
  };

  const fetchDocuments = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (category) params.append('category', category);
      if (instanceId === '__base__') params.append('baseOnly', 'true');
      else if (instanceId) params.append('instanceId', instanceId);
      params.append('sortBy', sortBy);
      params.append('limit', '200');

      const url = `${API_ENDPOINTS.RAG_DOCUMENTS()}?${params}`;
      const response = await fetch(url);
      if (!response.ok) throw new Error('Failed to fetch documents');

      const data = await response.json();
      setDocuments(data.documents || []);
      setMetadata(data.metadata || null);
    } catch (err) {
      console.error('Error fetching documents:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [search, category, sortBy, instanceId]);

  useEffect(() => { fetchDocuments(); }, [category, sortBy, instanceId]);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(fetchDocuments, 300);
    return () => clearTimeout(timer);
  }, [search]);

  const updateDocument = useCallback(async (docId, updates) => {
    try {
      const res = await fetch(API_ENDPOINTS.RAG_DOCUMENT(docId), {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(updates),
      });
      if (!res.ok) throw new Error('Update failed');
      const result = await res.json();
      // Update list + the modal's open document (if it matches) so edits
      // visible immediately without a refetch.
      setDocuments(prev => prev.map(d => d.id === docId ? { ...d, ...result } : d));
      setSelectedDoc(prev => (prev && prev.id === docId) ? { ...prev, ...result } : prev);
      return result;
    } catch (err) {
      console.error('Failed to update document:', err);
      throw err;
    }
  }, []);

  const handleWeightChange = useCallback((e, docId) => {
    e.stopPropagation();
    const value = parseFloat(e.target.value);
    // Optimistic local update
    setDocuments(prev => prev.map(d =>
      d.id === docId ? { ...d, admin_weight: value } : d
    ));
  }, []);

  const handleWeightCommit = useCallback((e, docId) => {
    e.stopPropagation();
    updateDocument(docId, { admin_weight: parseFloat(e.target.value) });
  }, [updateDocument]);

  const handleToggleExclude = useCallback((e, doc) => {
    e.stopPropagation();
    updateDocument(doc.id, { is_excluded: !doc.is_excluded });
  }, [updateDocument]);

  return (
    <Container>
      <AdminHeader />

      <Content>
        {tabs}
        <Header>
          <TitleSection>
            <Title>
              <Book size={28} />
              Aineistot
            </Title>
            <Subtitle>RAG-tietokanta: tutkimusdokumentit, lähteet ja laadunhallinta</Subtitle>
          </TitleSection>

          <StatsRow>
            <StatBadge>
              <FileText size={14} />
              {metadata?.total || 0} dokumenttia
            </StatBadge>
            <StatBadge>
              <Hash size={14} />
              {metadata?.synopsisStats?.withSynopsis || 0} tiivistelmää
            </StatBadge>
            <StatBadge
              as="button"
              onClick={handleRagExport}
              disabled={!instanceId || instanceId === '__base__'}
              title={instanceId && instanceId !== '__base__'
                ? 'Lataa instanssin koko RAG-sisältö (chunkit + rikastukset) JSON-tiedostona'
                : 'Valitse ensin instanssi'}
              style={{ cursor: instanceId && instanceId !== '__base__' ? 'pointer' : 'not-allowed' }}
            >
              <Download size={14} />
              Vie RAG JSON
            </StatBadge>
          </StatsRow>
        </Header>

        <Filters>
          <InstanceSelect value={instanceId} onChange={(e) => setInstanceId(e.target.value)}>
            <option value="">Kaikki aineistot</option>
            <option value="__base__">Perusaineisto (Kuutio-base)</option>
            {instances.map(inst => (
              <option key={inst.id} value={inst.id}>
                {inst.name || inst.title || inst.id.slice(0, 8)}
              </option>
            ))}
          </InstanceSelect>

          <SearchInput>
            <Search />
            <input
              type="text"
              placeholder="Hae dokumenteista..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </SearchInput>

          <Select value={category} onChange={(e) => setCategory(e.target.value)}>
            <option value="">Kaikki kategoriat</option>
            {metadata?.categories?.map(cat => (
              <option key={cat.category} value={cat.category}>
                {cat.category} ({cat.count})
              </option>
            ))}
          </Select>

          <Select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
            <option value="created_at">Uusimmat</option>
            <option value="title">Otsikko</option>
            <option value="admin_weight">Painotus</option>
            <option value="word_count">Pituus</option>
            <option value="category">Kategoria</option>
          </Select>
        </Filters>

        {loading ? (
          <LoadingContainer><Spinner /></LoadingContainer>
        ) : error ? (
          <EmptyState>
            <FileText size={40} />
            <h3>Virhe ladattaessa</h3>
            <p>{error}</p>
          </EmptyState>
        ) : documents.length === 0 ? (
          <EmptyState>
            <Book size={40} />
            <h3>Ei dokumentteja</h3>
            <p>Valitulle suodattimelle ei löytynyt dokumentteja.</p>
          </EmptyState>
        ) : (
          <DocumentGrid>
            {documents.map(doc => {
              const catColors = CATEGORY_COLORS[doc.category] || CATEGORY_COLORS.general;
              const sq = doc.source_quality || {};
              const cred = credibilityBadge(sq);
              const bias = biasBadge(sq);
              const weight = doc.admin_weight ?? 1;

              return (
                <DocumentCard
                  key={doc.id}
                  $categoryColor={catColors.gradient}
                  $excluded={doc.is_excluded}
                  onClick={() => setSelectedDoc(doc)}
                >
                  <CardHeader>
                    <CardTitle>{doc.title || doc.filename}</CardTitle>
                    <BadgeRow>
                      <CategoryBadge $bg={catColors.bg} $color={catColors.color}>
                        {doc.category || 'general'}
                      </CategoryBadge>
                    </BadgeRow>
                  </CardHeader>
                  {/* Raw author surfaced so admins can spot the bad ones (Unknown,
                      URL-slug filenames) and fix them via the detail modal. */}
                  {doc.author && doc.author.trim()
                    ? <Publisher title={doc.author}>{doc.author}</Publisher>
                    : <PublisherMissing title="Ei julkaisijaa — klikkaa korttia lisätäksesi">julkaisija puuttuu</PublisherMissing>}

                  {doc.synopsis && <Synopsis>{doc.synopsis}</Synopsis>}

                  <ControlRow>
                    {cred && (
                      <QualityBadge $bg={cred.bg} $color={cred.color}>
                        <Shield size={11} /> {cred.label}
                      </QualityBadge>
                    )}

                    <WeightControl>
                      <WeightSlider
                        type="range"
                        min="0"
                        max="2"
                        step="0.1"
                        value={weight}
                        onClick={(e) => e.stopPropagation()}
                        onChange={(e) => handleWeightChange(e, doc.id)}
                        onMouseUp={(e) => handleWeightCommit(e, doc.id)}
                        onTouchEnd={(e) => handleWeightCommit(e, doc.id)}
                        title={`Painotus: ${weight}`}
                      />
                      <WeightValue $v={weight}>{weight.toFixed(1)}</WeightValue>
                    </WeightControl>

                    <ExcludeBtn
                      $excluded={doc.is_excluded}
                      onClick={(e) => handleToggleExclude(e, doc)}
                      title={doc.is_excluded ? 'Ota käyttöön' : 'Poista käytöstä'}
                    >
                      {doc.is_excluded ? <><EyeOff size={10} /> Pois</> : <><Eye size={10} /> Käytössä</>}
                    </ExcludeBtn>
                  </ControlRow>

                  <TagRow>
                    {bias && (
                      <QualityBadge $bg={bias.bg} $color={bias.color}>
                        <AlertTriangle size={11} /> {bias.label}
                      </QualityBadge>
                    )}
                    {sq?.document_type && (
                      <QualityBadge $bg="rgba(255,255,255,0.05)" $color="#888">
                        {sq.document_type}
                      </QualityBadge>
                    )}
                    {sq?.evidence_type && sq.evidence_type !== sq.document_type && (
                      <QualityBadge $bg="rgba(99, 102, 241, 0.1)" $color="#818cf8">
                        {sq.evidence_type}
                      </QualityBadge>
                    )}
                  </TagRow>

                  <Stats>
                    <Stat><FileText /> {doc.total_pages || '-'} s.</Stat>
                    <Stat><Hash /> {doc.chunk_count || 0} chunkkia</Stat>
                    <Stat><Book /> {formatWordCount(doc.word_count)}</Stat>
                    <Stat><Calendar /> {formatDate(doc.created_at)}</Stat>
                  </Stats>
                </DocumentCard>
              );
            })}
          </DocumentGrid>
        )}
      </Content>

      {selectedDoc && (
        <KnowledgeDetailModal
          document={selectedDoc}
          onClose={() => setSelectedDoc(null)}
          onUpdate={updateDocument}
        />
      )}
    </Container>
  );
};

export default KnowledgeManager;
